import { Column, EmptyDiv, FormContext } from '@/components/SettingsLayout';
import SkeletonList from '@/components/SkeletonLoader/SkeletonList';
import { useGetUser } from '@/hooks/useGetUser';
import {
  useGetDefaultPricingConfigurationQuery,
  useGetMinimumPricingConfigurationQuery,
  useUpsertMinimumPricingConfigurationMutation,
} from '@/store/slices/pricingConfigurationSlice';
import { Text } from '@/ui';
import { skipToken } from '@reduxjs/toolkit/query';
import { useTranslation } from 'react-i18next';
import PricingConfigurationForm from '@/pages/pricing-forms/PricingConfigurationForm';
import PricingDiffFooter from '@/pages/pricing-forms/PricingDiffFooter';
import { usePricingForm } from '@/pages/pricing-forms/usePricingForm';

function FinalPricing() {
  const { t } = useTranslation();
  const { currentUser } = useGetUser();
  const organizationId = currentUser.companyId;
  const queryArg = organizationId ? { organizationId } : skipToken;

  const {
    data: defaults,
    isLoading: isDefaultsLoading,
    isError: isDefaultsError,
  } = useGetDefaultPricingConfigurationQuery(queryArg);
  const { data: minimums, isLoading: isMinimumsLoading } =
    useGetMinimumPricingConfigurationQuery(queryArg);

  const [upsertMinimumPricingConfig, { isLoading: isSaving }] =
    useUpsertMinimumPricingConfigurationMutation();

  const { activeCountry, setActiveCountry, submitHandler } = usePricingForm({
    baseline: defaults?.values ?? {},
    existing: minimums?.values,
    submit: (nextValues) =>
      upsertMinimumPricingConfig({
        organizationId,
        payload: { values: nextValues },
      }),
    messages: {
      success: t('Messages.minimum_pricing_update_success'),
      error: t('Messages.minimum_pricing_update_error'),
    },
  });

  if (isDefaultsLoading || isMinimumsLoading) {
    return <SkeletonList />;
  }

  if (isDefaultsError || !defaults) {
    return (
      <EmptyDiv>
        <Text size='s' weight='medium'>
          {t('Errors.error_loading_pricing')}
        </Text>
      </EmptyDiv>
    );
  }

  return (
    <FormContext submitHandler={submitHandler} mode='onChange' defaultValues={{ rows: [] }}>
      <Column gapSize='24'>
        <Column>
          <Text size='m' weight='semi-bold'>
            {t('Accounts.processing_fees')}
          </Text>
          <Text size='s' weight='light' color='base-grey'>
            {t('Accounts.processing_fees_comment')}
          </Text>
          <PricingConfigurationForm
            mode='minimum'
            defaults={defaults.values}
            existingValues={minimums?.values}
            isReadOnly={false}
            activeCountry={activeCountry}
            onActiveCountryChange={setActiveCountry}
          />
        </Column>
        <PricingDiffFooter
          baseline={defaults.values}
          existing={minimums?.values}
          isSaving={isSaving}
          isReadOnly={false}
          activeCountry={activeCountry}
        />
      </Column>
    </FormContext>
  );
}

export default FinalPricing;
